<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { listMeasurements, type Measurement } from '../api'
import AssignPatientModal from '../components/AssignPatientModal.vue'

const measurements = ref<Measurement[]>([])
const loading = ref(true)
const error = ref<string | null>(null)

const searchQuery = ref('')
const expanded = reactive<Set<string>>(new Set())
const copiedId = ref<string | null>(null)
const assignTarget = ref<Measurement | null>(null)

function onAssigned(updated: Measurement) {
  // Patch in place — keeps any expanded curve open and avoids a round-trip.
  const idx = measurements.value.findIndex((m) => m.id === updated.id)
  if (idx >= 0) measurements.value[idx] = updated
}

function fmtDateTime(s: string): string {
  return new Date(s).toLocaleString('de-AT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function fmtNum(n: number | null, digits = 1): string {
  return n == null ? '–' : n.toFixed(digits)
}

// Sort by patient name (de-DE locale) asc, then date desc within patient;
// then walk the sorted list and emit groups.
interface Group { patientKey: string; patientLabel: string; rows: Measurement[] }

const groups = computed<Group[]>(() => {
  const q = searchQuery.value.trim().toLowerCase()
  const filtered = q
    ? measurements.value.filter((m) =>
        (m.patient_name || '').toLowerCase().includes(q),
      )
    : measurements.value.slice()

  filtered.sort((a, b) => {
    // Unassigned sorts last
    const an = a.patient_name ?? '￿'
    const bn = b.patient_name ?? '￿'
    const byName = an.localeCompare(bn, 'de')
    if (byName !== 0) return byName
    return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  })

  const out: Group[] = []
  for (const m of filtered) {
    const key = m.patient_id ?? 'unassigned'
    const label = m.patient_name ?? 'Nicht zugeordnet'
    const last = out[out.length - 1]
    if (last && last.patientKey === key) {
      last.rows.push(m)
    } else {
      out.push({ patientKey: key, patientLabel: label, rows: [m] })
    }
  }
  return out
})

const totalShown = computed(() =>
  groups.value.reduce((n, g) => n + g.rows.length, 0),
)

async function reload() {
  loading.value = true
  error.value = null
  try {
    measurements.value = await listMeasurements()
  } catch {
    error.value = 'Daten konnten nicht geladen werden.'
  } finally {
    loading.value = false
  }
}

function toggleCurve(m: Measurement) {
  if (expanded.has(m.id)) expanded.delete(m.id)
  else expanded.add(m.id)
}

function buildCopyText(m: Measurement): string {
  const patient = m.patient_name ?? 'Nicht zugeordnet'
  return [
    `Uroflowmetrie vom ${fmtDateTime(m.timestamp)} – ${patient}`,
    `  Harnvolumen: ${fmtNum(m.voided_volume_ml, 0)} ml`,
    `  Qmax: ${fmtNum(m.max_flow_ml_s, 1)} ml/s`,
    `  Qavg: ${fmtNum(m.avg_flow_ml_s, 1)} ml/s`,
    `  Miktionszeit: ${fmtNum(m.duration_s, 1)} s`,
    `  Zeit bis Qmax: ${fmtNum(m.time_to_max_s, 1)} s`,
  ].join('\n')
}

async function onCopy(m: Measurement) {
  try {
    await navigator.clipboard.writeText(buildCopyText(m))
    copiedId.value = m.id
    setTimeout(() => {
      if (copiedId.value === m.id) copiedId.value = null
    }, 1800)
  } catch {
    error.value = 'Kopieren fehlgeschlagen (Browser-Berechtigung?).'
  }
}

onMounted(reload)
</script>

<template>
  <div class="page">
    <div class="header">
      <h1>Messungen</h1>
      <div class="toolbar">
        <input
          v-model="searchQuery"
          type="search"
          class="search"
          placeholder="Patient/in suchen…"
        />
        <button @click="reload" :disabled="loading">Aktualisieren</button>
      </div>
    </div>

    <div v-if="loading" class="state muted">Lade…</div>
    <div v-else-if="error" class="state error">{{ error }}</div>
    <div v-else-if="!totalShown" class="state muted">
      {{ searchQuery ? 'Keine Treffer.' : 'Noch keine Messungen vorhanden.' }}
    </div>

    <table v-else>
      <thead>
        <tr>
          <th>Datum</th>
          <th class="num">Volumen</th>
          <th class="num">Qmax</th>
          <th class="num">Qavg</th>
          <th class="num">Dauer</th>
          <th class="actions">Aktionen</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="g in groups" :key="g.patientKey">
          <tr class="group-header">
            <td colspan="6">
              <span class="patient-label">{{ g.patientLabel }}</span>
              <span class="patient-count">{{ g.rows.length }} Messung{{ g.rows.length === 1 ? '' : 'en' }}</span>
            </td>
          </tr>

          <template v-for="m in g.rows" :key="m.id">
            <tr>
              <td>{{ fmtDateTime(m.timestamp) }}</td>
              <td class="num">{{ fmtNum(m.voided_volume_ml, 0) }} ml</td>
              <td class="num">{{ fmtNum(m.max_flow_ml_s, 1) }} ml/s</td>
              <td class="num">{{ fmtNum(m.avg_flow_ml_s, 1) }} ml/s</td>
              <td class="num">{{ fmtNum(m.duration_s, 1) }} s</td>
              <td class="actions">
                <button class="link" @click="assignTarget = m">
                  {{ m.patient_id ? 'Patient ändern' : 'Patient zuordnen' }}
                </button>
                <button class="link" @click="onCopy(m)">
                  {{ copiedId === m.id ? 'Kopiert ✓' : 'Kopieren' }}
                </button>
                <button class="link" @click="toggleCurve(m)">
                  {{ expanded.has(m.id) ? 'Kurve verbergen' : 'Kurve anzeigen' }}
                </button>
                <a
                  class="link"
                  :href="`/api/measurements/${m.id}/pdf`"
                  target="_blank"
                  rel="noopener"
                >PDF</a>
              </td>
            </tr>
            <tr v-if="expanded.has(m.id)" class="curve-row">
              <td colspan="6">
                <img
                  :src="`/api/measurements/${m.id}/curve.svg`"
                  alt="Flusskurve"
                  class="curve"
                />
              </td>
            </tr>
          </template>
        </template>
      </tbody>
    </table>

    <AssignPatientModal
      v-if="assignTarget"
      :measurement="assignTarget"
      @assigned="onAssigned"
      @close="assignTarget = null"
    />
  </div>
</template>

<style scoped>
.page { max-width: 1280px; margin: 0 auto; }
.header {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 20px; gap: 16px;
}
h1 {
  font-family: var(--font-display); font-weight: 500;
  font-size: 30px; letter-spacing: 0.01em;
}
.toolbar { display: flex; gap: 10px; align-items: center; }
.search { min-width: 260px; }

.state {
  padding: 36px; text-align: center; background: var(--color-surface);
  border-radius: var(--radius); border: 1px solid var(--color-border);
}
.muted { color: var(--color-muted); }
.error { color: var(--color-danger); }

table {
  width: 100%;
  border-collapse: collapse;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}
th, td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid var(--color-border);
  font-size: 14px;
}
th {
  background: var(--light-beige);
  font-weight: 500;
  color: var(--color-muted);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-family: var(--font-mono);
}
tbody tr:last-child td { border-bottom: none; }
tbody tr:hover:not(.group-header):not(.curve-row) { background: var(--light-beige); }

.num {
  text-align: right;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 13px;
}
.actions { text-align: right; white-space: nowrap; }

.group-header td {
  background: var(--beige);
  padding: 10px 16px;
  border-top: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
}
.patient-label {
  font-family: var(--font-display);
  font-weight: 500;
  color: var(--color-text);
  font-size: 15px;
}
.patient-count { color: var(--color-muted); font-size: 12px; margin-left: 12px; }

button.link, a.link {
  border: none; background: transparent; padding: 5px 9px;
  color: var(--color-primary); font-size: 13px; font-weight: 500;
  text-decoration: none; display: inline-block;
  border-radius: 6px;
  cursor: pointer; font-family: inherit;
}
button.link:hover:not(:disabled), a.link:hover {
  background: rgba(var(--salvia-rgb), 0.12);
  color: var(--salvia-dark);
  text-decoration: none;
}

.curve-row td { background: var(--light-beige); padding: 18px; }
.curve {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  background: var(--white);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
}
</style>
